import { Children, Component, ReturnComponent } from '@/utils/reactTypes';
import { Tooltip, TooltipProps } from '@mui/material';
import { ReactElement } from 'react';
import { Link } from 'react-router';

/* Conditionally wrap a component with a <Link/>. */
export const ConditionalLink = ({
    to,
    condition,
    children,
    ...rest
}: {
    to: string;
    condition: boolean;
    children: Children;
}): ReturnComponent => {
    return condition ? (
        <Link to={to} {...rest}>
            {children}
        </Link>
    ) : (
        <>{children}</>
    );
};

// Stable identities so Emotion does not re-serialize every tooltip's popper on
//  each render of a chip- or tooltip-heavy list.
const NO_SLOT_PROPS: TooltipProps['slotProps'] = {};

const POPPER_PROPS = {
    sx: {
        '&[data-popper-placement*="right"]': {
            cursor: 'pointer',
        },
    },
};

/* Conditionally add a tooltip to a component. */
export const ConditionalTooltip = ({
    title,
    condition,
    children,
    slotProps = NO_SLOT_PROPS,
    placement,
    ...rest
}: {
    title: string | Component;
    condition: boolean;
    slotProps?: TooltipProps['slotProps'];
    placement?: TooltipProps['placement'];
    // For <Tooltip/> to work, it expects a ReactElement, not null or a string or
    //  anything else; hence we use ReactElement as the type here.
    children: ReactElement;
}): ReturnComponent => {
    // Skip the Tooltip entirely when there is nothing to show, so we don't mount
    //  a Popper and its hover machinery per child in chip-heavy lists.
    if (!condition || !title) {
        return <>{children}</>;
    }

    return (
        <Tooltip
            title={title}
            slotProps={slotProps}
            placement={placement}
            {...rest}
            PopperProps={POPPER_PROPS}
        >
            {children}
        </Tooltip>
    );
};

/* Conditionally render a component.
 *
 * Usage:
 *
 *   <If condition={showAffirmation}>
 *     <div>You are frighteningly courageous.</div>
 *   </If>
 *
 * */
export function If({
    condition,
    children,
}: {
    condition: boolean;
    children: Children;
}): ReturnComponent {
    if (condition) {
        // We do ` || null` because some of our Javascript code seems to pass in an
        //  empty, non-renderable value that makes React cause an error. Not sure.
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        return <>{children || null}</>;
    } else {
        return null;
    }
}

/* Conditional wrapper */
export const ConditionalWrapper = ({
    condition,
    wrapper,
    children,
}: {
    condition: boolean;
    wrapper: (children: Component) => ReturnComponent;
    children: Component;
}): Component => (condition ? wrapper(children) : children);
